import type { FinalJob } from '../lambda/types';
import type { DisplayJob } from '../types';

type SchemaJobLocation = {
  addressLocality?: string;
  addressRegion?: string;
  addressCountry?: string;
};

export type JobDetailNormalizationOptions = {
  locale: string;
  seoSlug?: string;
};

export type JobDetailNormalizationResult = {
  displayJob: DisplayJob;
  canonicalJob: FinalJob;
  schemaOverrides: {
    jobLocationType?: 'ON_SITE' | 'HYBRID' | 'TELECOMMUTE';
    jobLocations?: SchemaJobLocation[];
    applicantLocationRequirements?: Record<string, unknown> | Record<string, unknown>[];
    disableSalary?: boolean;
  };
  visibleText: string;
  notes: string[];
};

const HELSINKI_LABELS: Record<string, string> = {
  en: 'Helsinki, Finland',
  fi: 'Helsinki, Suomi',
  sv: 'Helsingfors, Finland',
};

const REMOTE_LOCATION_LABELS: Record<string, string> = {
  en: 'Remote - Finland',
  fi: 'Etätyö - Suomi',
  sv: 'Distans - Finland',
};

const REMOTE_REMOTENESS_LABELS: Record<string, string> = {
  en: 'Remote in Finland',
  fi: 'Etätyö Suomessa',
  sv: 'Distansarbete i Finland',
};

const HYBRID_REMOTENESS_LABELS: Record<string, string> = {
  en: 'Hybrid (Helsinki office)',
  fi: 'Hybridi (Helsinki-toimisto)',
  sv: 'Hybrid (Helsingfors-kontor)',
};

function cloneDisplayJob(job: DisplayJob): DisplayJob {
  const cloned: DisplayJob = { ...job };
  if (Array.isArray(job.location)) {
    cloned.location = [...job.location];
  }
  if (Array.isArray(job.requirements)) {
    cloned.requirements = [...job.requirements];
  }
  if (Array.isArray(job.responsibilities)) {
    cloned.responsibilities = [...job.responsibilities];
  }
  if (Array.isArray(job.skillsAndTechnologies)) {
    cloned.skillsAndTechnologies = [...job.skillsAndTechnologies];
  }
  if (Array.isArray(job.specialisations)) {
    cloned.specialisations = [...job.specialisations];
  }
  if (Array.isArray(job.requiredLanguages)) {
    cloned.requiredLanguages = [...job.requiredLanguages];
  }
  if (Array.isArray(job.optionalLanguages)) {
    cloned.optionalLanguages = [...job.optionalLanguages];
  }
  if (Array.isArray(job.benefits)) {
    cloned.benefits = [...job.benefits];
  }
  if (Array.isArray(job.recruitmentProcess)) {
    cloned.recruitmentProcess = [...job.recruitmentProcess];
  }
  if (Array.isArray(job.perks)) {
    cloned.perks = [...job.perks];
  }
  if (Array.isArray(job.companyCoreValues)) {
    cloned.companyCoreValues = [...job.companyCoreValues];
  }
  if (Array.isArray(job.companyAwardsAndCertifications)) {
    cloned.companyAwardsAndCertifications = [...job.companyAwardsAndCertifications];
  }
  return cloned;
}

function cloneFinalJob(job: FinalJob): FinalJob {
  const cloned: FinalJob = { ...job };
  const cloneLocaleArray = (key: 'location_en' | 'location_fi' | 'location_sv') => {
    const value = job[key];
    if (Array.isArray(value)) {
      cloned[key] = [...value];
    }
  };
  cloneLocaleArray('location_en');
  cloneLocaleArray('location_fi');
  cloneLocaleArray('location_sv');
  return cloned;
}

function normalizeLocationsArray(value: DisplayJob['location']): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry) => entry.length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  return [];
}

function ensureUnique(list: string[], value: string): string[] {
  const lower = value.toLowerCase();
  const existing = list.find((entry) => entry.toLowerCase() === lower);
  if (existing) return list;
  return [value, ...list];
}

function gatherVisibleText(job: DisplayJob): string {
  const fragments: string[] = [];
  const push = (val: unknown) => {
    if (!val) return;
    if (typeof val === 'string') {
      fragments.push(val);
    } else if (Array.isArray(val)) {
      val.forEach((entry) => {
        if (typeof entry === 'string') {
          fragments.push(entry);
        }
      });
    }
  };

  push(job.description);
  push(job.requirements);
  push(job.responsibilities);
  push(job.skillsAndTechnologies);
  push(job.perks);
  push(job.benefits);
  push(job.recruitmentProcess);
  if (job.salaryDetails) {
    push(job.salaryDetails);
  }
  return fragments.join('\n');
}

function isFullyRemote(job: DisplayJob, visibleText: string): boolean {
  const remoteness = (job.remoteness || '').toLowerCase();
  if (remoteness.includes('hybrid')) return false;
  const text = visibleText.toLowerCase();
  const remoteIndicators = [
    'fully remote',
    '100% remote',
    'remote in finland',
    'work remotely from finland',
    'remote-first',
  ];
  const hasRemoteIndicator =
    remoteness.includes('remote') ||
    remoteIndicators.some((indicator) => text.includes(indicator));
  const locationList = normalizeLocationsArray(job.location).map((entry) =>
    entry.toLowerCase()
  );
  const locationIndicatesRemote = locationList.some(
    (loc) => loc.includes('remote') || loc.includes('anywhere')
  );
  return hasRemoteIndicator && locationIndicatesRemote;
}

function createSchemaLocations(locations: SchemaJobLocation[] | undefined): SchemaJobLocation[] | undefined {
  if (!locations || locations.length === 0) return undefined;
  return locations.map((loc) => ({
    ...(loc.addressLocality ? { addressLocality: loc.addressLocality } : {}),
    ...(loc.addressRegion ? { addressRegion: loc.addressRegion } : {}),
    ...(loc.addressCountry ? { addressCountry: loc.addressCountry } : {}),
  }));
}

export function normalizeJobDetail(
  job: FinalJob,
  displayJob: DisplayJob,
  options: JobDetailNormalizationOptions
): JobDetailNormalizationResult {
  const normalizedDisplayJob = cloneDisplayJob(displayJob);
  const canonicalJob = cloneFinalJob(job);
  const schemaOverrides: JobDetailNormalizationResult['schemaOverrides'] = {};
  const visibleText = gatherVisibleText(normalizedDisplayJob);
  const notes: string[] = [];

  const slug = (options.seoSlug || '').toLowerCase();
  const slugSuggestsHybridHelsinki =
    slug.includes('hybrid') && slug.includes('helsinki') && slug.includes('finland');

  const locale = options.locale as keyof typeof HELSINKI_LABELS;
  const helsinkiLabel = HELSINKI_LABELS[locale] ?? HELSINKI_LABELS.en;
  const remoteLocationLabel =
    REMOTE_LOCATION_LABELS[locale] ?? REMOTE_LOCATION_LABELS.en;
  const remoteRemotenessLabel =
    REMOTE_REMOTENESS_LABELS[locale] ?? REMOTE_REMOTENESS_LABELS.en;
  const hybridRemotenessLabel =
    HYBRID_REMOTENESS_LABELS[locale] ?? HYBRID_REMOTENESS_LABELS.en;

  let locationList = normalizeLocationsArray(normalizedDisplayJob.location);
  locationList = locationList.filter((entry, index, arr) => {
    const lower = entry.toLowerCase();
    return (
      lower.length > 0 &&
      index === arr.findIndex((candidate) => candidate.toLowerCase() === lower)
    );
  });

  const fullyRemote = isFullyRemote(normalizedDisplayJob, visibleText);

  if (slugSuggestsHybridHelsinki) {
    if (fullyRemote) {
      schemaOverrides.jobLocationType = 'TELECOMMUTE';
      schemaOverrides.applicantLocationRequirements = { '@type': 'Country', name: 'Finland' };
      schemaOverrides.jobLocations = undefined;
      locationList = ensureUnique(
        locationList.filter((loc) => !loc.toLowerCase().includes('helsinki')),
        remoteLocationLabel
      );
      normalizedDisplayJob.location = locationList;
      normalizedDisplayJob.remoteness = remoteRemotenessLabel;

      const setLocaleLocation = (localeKey: 'en' | 'fi' | 'sv', value: string) => {
        const key = `location_${localeKey}` as keyof FinalJob;
        const existing = canonicalJob[key];
        const updated = Array.isArray(existing)
          ? ensureUnique(existing as string[], value)
          : [value];
        canonicalJob[key] = updated as any;
      };
      setLocaleLocation('en', REMOTE_LOCATION_LABELS.en);
      setLocaleLocation('fi', REMOTE_LOCATION_LABELS.fi);
      setLocaleLocation('sv', REMOTE_LOCATION_LABELS.sv);
      notes.push('Slug indicates hybrid Helsinki but job appears fully remote; normalizing to TELECOMMUTE.');
    } else {
      schemaOverrides.jobLocations = createSchemaLocations([
        { addressLocality: 'Helsinki', addressCountry: 'FI' },
      ]);
      locationList = ensureUnique(locationList, helsinkiLabel);
      normalizedDisplayJob.location = locationList;
      normalizedDisplayJob.remoteness = hybridRemotenessLabel;

      const setLocaleLocation = (
        localeKey: 'en' | 'fi' | 'sv',
        value: string
      ) => {
        const key = `location_${localeKey}` as keyof FinalJob;
        const existing = canonicalJob[key];
        const updated = Array.isArray(existing)
          ? ensureUnique(existing as string[], value)
          : [value];
        canonicalJob[key] = updated as any;
      };
      setLocaleLocation('en', HELSINKI_LABELS.en);
      setLocaleLocation('fi', HELSINKI_LABELS.fi);
      setLocaleLocation('sv', HELSINKI_LABELS.sv);
      notes.push('Slug indicates hybrid Helsinki; enforcing Helsinki office in schema.');
    }
  } else {
    normalizedDisplayJob.location = locationList;
  }

  // Salary visibility heuristics: require both min/max values to appear in visible text.
  const checkSalaryVisibility = (): boolean => {
    const { salaryRangeMin, salaryRangeMax } = normalizedDisplayJob;
    if (
      typeof salaryRangeMin !== 'number' ||
      Number.isNaN(salaryRangeMin) ||
      salaryRangeMin <= 0
    ) {
      return false;
    }
    if (
      typeof salaryRangeMax !== 'number' ||
      Number.isNaN(salaryRangeMax) ||
      salaryRangeMax <= 0
    ) {
      return false;
    }
    if (!normalizedDisplayJob.salaryPeriod) {
      return false;
    }
    const text = visibleText.toLowerCase();
    const valuePatterns = (value: number) => {
      const raw = Math.round(value).toString();
      const spaced = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const dotted = raw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const comma = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return Array.from(new Set([raw, spaced, dotted, comma]));
    };

    const containsValue = (patterns: string[]) =>
      patterns.some((pattern) => text.includes(pattern.toLowerCase()));

    const minMatches = containsValue(valuePatterns(salaryRangeMin));
    const maxMatches = containsValue(valuePatterns(salaryRangeMax));
    if (!minMatches || !maxMatches) {
      return false;
    }
    return true;
  };

  const salaryVisible = checkSalaryVisibility();
  if (!salaryVisible) {
    schemaOverrides.disableSalary = true;
    notes.push('Salary values not visibly confirmed; omitting salary from structured data.');
  }

  return {
    displayJob: normalizedDisplayJob,
    canonicalJob,
    schemaOverrides,
    visibleText,
    notes,
  };
}

